import { FineUploaderBasic } from 'fine-uploader/lib/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from 'components/atoms/Icon';
import LoadingSpinner from 'components/atoms/LoadingSpinner';
import { useToast } from 'components/molecules/Toast';
import { refreshTokenChatbotService } from 'services/accountConversations';
import { getLocalStorage, setLocalStorage } from 'services/common/storage';
import { LOCAL_STORAGE_CHATBOT } from 'utils/constants';
import mapModifiers from 'utils/functions';

export type RecordStatusType = 'starting' | 'processing' | 'ended' | 'noRecord';

interface SpeechToTextProps {
  conversationCode: string;
  disabled?: boolean;
  onRecordingText: (text?: string) => void;
  onCloseStartVoice: () => void;
  isRecording?: boolean;
  onClose: () => void;
  onMicrophonePermissionsDenied: () => void;
  mainColor?: string;
}

type RecordParamType = {
  noRecording?: boolean;
};

const SpeechToText: React.FC<SpeechToTextProps> = ({
  conversationCode,
  onRecordingText,
  disabled,
  isRecording,
  onCloseStartVoice,
  onClose,
  onMicrophonePermissionsDenied,
  mainColor,
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const botId = (window as any).currentBotPath;

  const [listening, setListening] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recordStatus, setRecordStatus] = useState<RecordStatusType>('noRecord');

  const idUpload = useRef<number | null>(null);
  const animationFrameVoiceRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopAfter1Minute = useRef<NodeJS.Timeout | null>(null);
  const retryUpload = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const uploadTimerRef = useRef<any>(null);

  const totalDuration = 60000; // 1 minute
  // const segmentDuration = 5000; // 5 seconds
  const threshold = 8; // Adjust based on noise level

  const stopRecording = (params: RecordParamType) => {
    setListening(false);

    if (animationFrameVoiceRef.current) {
      console.log('Cancel animation frame voice');
      cancelAnimationFrame(animationFrameVoiceRef.current);
      animationFrameVoiceRef.current = null;
    }

    if (stopAfter1Minute.current) {
      clearInterval(stopAfter1Minute.current);
      stopAfter1Minute.current = null;
      console.log('Clear Stop After 1 Minute');
    }

    if (mediaRecorderRef.current) {
      if (params.noRecording) {
        mediaRecorderRef.current.ondataavailable = null;
      }
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const uploader = new FineUploaderBasic({
    autoUpload: false,
    request: {
      endpoint: `api/v1/account-s2t/${conversationCode}/upload-chunk`,
      customHeaders: {
        'X-Bot-Key': getLocalStorage(LOCAL_STORAGE_CHATBOT.BOTKEY + botId) || '',
        Authorization: `Bearer ${getLocalStorage(LOCAL_STORAGE_CHATBOT.ACCESSTOKEN_CHATBOT + botId)}` || '',
      },
    },
    chunking: {
      enabled: true,
      mandatory: true,
      partSize: 10000000, // 10MB,
      success: {
        jsonPayload: true,
        endpoint: `api/v1/account-s2t/${conversationCode}/merge-chunk`,
        params(fileId: number) {
          const uuid = uploader.getUuid(fileId);
          const fileName = (uploader.getFile(fileId) as File).name;
          return {
            chunkedUuid: uuid,
            qqfilename: fileName,
          };
        },
      },
    },
    callbacks: {
      onUpload: (id: number, name: string) => {
        console.log('🚀 ~ name:', name);
        idUpload.current = id;
        setUploading(true);

        // Clear any existing timer
        if (uploadTimerRef.current) {
          clearTimeout(uploadTimerRef.current);
        }

        // Set a timer to cancel if process exceeds 30 seconds
        uploadTimerRef.current = setTimeout(() => {
          // Cancel the upload
          uploader.cancel(id);
          setUploading(false);

          // Show toast notification about the cancellation
          showToast({
            message: 'Xử lý quá lâu. Vui lòng thử lại.',
            show: true,
            type: 'error',
          });

          onClose();
        }, 30000);
      },
      onComplete(
        id: number,
        name: string,
        res: {
          success: boolean;
          data?: {
            text: string;
            code: string;
          };
        }
      ) {
        // Clear the timer since upload completed successfully
        if (uploadTimerRef.current) {
          clearTimeout(uploadTimerRef.current);
          uploadTimerRef.current = null;
        }

        retryUpload.current = false;
        setUploading(false);
        if (res.success) {
          onRecordingText(res?.data?.text);
          onClose();
        }
      },
      onError: async (id: number, name: string, errorReason: string, xhr: XMLHttpRequest) => {
        // Clear the timer
        if (uploadTimerRef.current) {
          clearTimeout(uploadTimerRef.current);
          uploadTimerRef.current = null;
        }

        setUploading(false);
        if (xhr && xhr.status === 401 && !retryUpload.current) {
          try {
            const responseRefreshToken = await refreshTokenChatbotService();
            if (responseRefreshToken.accessToken) {
              setLocalStorage(LOCAL_STORAGE_CHATBOT
                .ACCESSTOKEN_CHATBOT, responseRefreshToken.accessToken);
              setLocalStorage(LOCAL_STORAGE_CHATBOT
                .REFRESHTOKEN_CHATBOT, responseRefreshToken.refreshToken);
              uploader.setCustomHeaders({
                Authorization: `Bearer ${responseRefreshToken.accessToken}` || '',
              });
              // Retry the upload
              uploader.retry(id);
            }
          } catch (error) {
            console.error('Token refresh failed:', error);
          } finally {
            retryUpload.current = true;
          }
          return;
        }
        if (xhr && xhr.status === 408) {
          showToast({
            message: 'Xử lý quá lâu. Vui lòng thử lại.',
            show: true,
            type: 'error',
          });
        }
        onClose();
      },
    },
  });

  const handleCleanRecording = (params: RecordParamType) => {
    if (idUpload.current) {
      console.log(`Upload with ID ${idUpload.current} has been cancelled.`);
      uploader?.cancel(idUpload.current);
    }

    chunksRef.current = [];
    stopRecording({ noRecording: params.noRecording });
    onClose();
    // setHasText(false);
    console.log('Cleanup completed');
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        audioBitsPerSecond: 128000, // Chất lượng âm thanh là 128 kbps
      });
      mediaRecorderRef.current = recorder;

      // alert(`State MediaRecorder ${JSON.stringify(mediaRecorderRef.current?.state)}`);

      let analyser: any;
      let dataArrayFrequency: any;
      if (window?.AudioContext || (window as any)?.webkitAudioContext) {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const mediaStreamSource = audioContext.createMediaStreamSource(stream);
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        mediaStreamSource.connect(analyser);
        // alert('AudioContext initialized successfully');
        dataArrayFrequency = new Uint8Array(analyser.frequencyBinCount);
      }

      mediaRecorderRef.current.ondataavailable = (event) => {
        const audioBlob = event.data;
        if (audioBlob.size > 0) {
          chunksRef.current = [...chunksRef.current, audioBlob];
        }
      };

      console.log('Start Record');
      const startTime = Date.now();
      mediaRecorderRef.current?.start();
      setRecordStatus('starting');

      const checkVoice = () => {
        if (analyser) {
          const dataArray = new Uint8Array(analyser.fftSize);
          analyser.getByteTimeDomainData(dataArray);
          let isVoiceDetected = false;

          for (let i = 0; i < dataArray.length; i += 1) {
            if (Math.abs(dataArray[i] - 128) > threshold) {
              // console.log('Voice detected, starting recording...');
              setListening(true);
              isVoiceDetected = true;
              break;
            }
          }

          if (!isVoiceDetected && mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            setListening(false);
            // console.log('No voice detected, stopping recording...');
          }

          animationFrameVoiceRef.current = requestAnimationFrame(checkVoice);
        }
      };

      const updateTimer = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;

        const elapsedTime = Date.now() - startTime;
        const seconds = Math.floor((elapsedTime / 1000) % 60)
          .toString()
          .padStart(2, '0');
        const minutes = Math.floor((elapsedTime / (1000 * 60)) % 60)
          .toString()
          .padStart(2, '0');

        // Update a time display element
        const timerElement = document.getElementById('recording-timer');
        if (timerElement) {
          timerElement.textContent = `${minutes}:${seconds}`;
        }

        // Continue updating
        setTimeout(updateTimer, 1000);
      };

      // Start the timer
      updateTimer();

      const drawWaveform = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
          // Canvas not ready yet, try again in the next frame
          animationRef.current = requestAnimationFrame(drawWaveform);
          return;
        }
        if (!canvas || !analyser || !dataArrayFrequency) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const parent = canvas.parentNode;
        canvas.width = parseInt(getComputedStyle(parent as HTMLElement).width, 10) || canvas.width;
        const { width, height } = canvas;

        analyser.getByteFrequencyData(dataArrayFrequency);

        ctx.clearRect(0, 0, width, height);

        const barWidth = 2;
        const barSpacing = 2;

        // Calculate how many bars can fit in the canvas width
        const numBars = Math.floor(width / (barWidth + barSpacing)) - 13;

        ctx.fillStyle = '#ffffff';
        for (let i = 0; i < numBars; i += 1) {
          // Get data value, using a step size to cover the full spectrum with our
          // limited number of bars
          const dataIndex = Math.floor(i * (dataArrayFrequency.length / numBars));

          // Scale the bar height based on the data value
          const barHeight = Math.max((dataArrayFrequency[dataIndex] / 255) * height, 2);

          // Calculate x position with spacing
          const x = i * (barWidth + barSpacing) + 8; // Add 8px padding

          // Calculate half height for centering
          const halfHeight = barHeight / 2;
          const centerY = height / 2;

          // Draw the bar body from the center of the canvas
          if (barHeight > 0) {
            // Top rounded cap
            ctx.beginPath();
            ctx.arc(x + barWidth / 2, centerY - halfHeight, barWidth / 2, 0, Math.PI, true);
            ctx.fill();

            // Main bar rectangle
            ctx.fillRect(x, centerY - halfHeight, barWidth, barHeight);

            // Bottom rounded cap
            ctx.beginPath();
            ctx.arc(x + barWidth / 2, centerY + halfHeight, barWidth / 2, 0, Math.PI, false);
            ctx.fill();
          }
        }

        animationRef.current = requestAnimationFrame(drawWaveform);
      };

      drawWaveform();

      checkVoice();

      mediaRecorderRef.current.onstop = () => {
        if (chunksRef.current && chunksRef.current.length > 0) {
          try {
            setRecordStatus('processing');
            const nameFile = `recording-${new Date().getFullYear()}-${new Date().getMonth() + 1
              }-${new Date().getDate()}-${new Date().getTime()}`;
            const { type } = chunksRef.current[0];
            let extensionFile = chunksRef.current[0].type?.split('/')[1] || 'mp3';
            if (chunksRef.current[0].type.includes('audio/flac')) {
              extensionFile = 'flac';
            } else if (
              chunksRef.current[0].type.includes('audio/mp4')
              || chunksRef.current[0].type.includes('video/mp4')
            ) {
              extensionFile = 'mp4';
            } else if (chunksRef.current[0].type.includes('audio/x-m4a')) {
              extensionFile = 'm4a';
            } else if (chunksRef.current[0].type.includes('audio/mpeg')) {
              extensionFile = 'mpeg';
            } else if (chunksRef.current[0].type.includes('audio/mp3')) {
              extensionFile = 'mp3';
            } else if (
              chunksRef.current[0].type.includes('audio/ogg')
              || chunksRef.current[0].type.includes('application/ogg')
            ) {
              extensionFile = 'ogg';
            } else if (
              chunksRef.current[0].type.includes('audio/wav')
              || chunksRef.current[0].type.includes('application/x-wav')
            ) {
              extensionFile = 'wav';
            } else if (
              chunksRef.current[0].type.includes('audio/webm')
              || chunksRef.current[0].type.includes('video/webm')
            ) {
              extensionFile = 'webm';
            }
            // alert(`Type origin: ${chunksRef.current[0].type} -
            // Extension detect: ${extensionFile}`);
            const audioBlob = new Blob(chunksRef.current);
            const audioFile = new File([audioBlob], `${nameFile}.${extensionFile}`, { type });

            uploader.addFiles(audioFile);
            uploader.uploadStoredFiles();

            // const audioURL = URL.createObjectURL(audioFile);
            // const audioRes = new Audio(audioURL);
            // audioRes.play();

            // const element = document.createElement('a');
            // element.download = nameFile;
            // element.href = audioURL;

            // element.style.display = 'none';
            // document.body.appendChild(element);

            // element.click();

            // document.body.removeChild(element);
          } catch {
            // Throw
          }
        }
      };

      stopAfter1Minute.current = setInterval(() => {
        stopRecording({ noRecording: true });
        onClose();
        onCloseStartVoice();
      }, totalDuration);
    } catch (error) {
      console.error('Error:', error);
      onMicrophonePermissionsDenied();
      onClose();
    }
  };

  const handleOnMic = () => {
    stopRecording({});
  };

  const detectMicButton = () => {
    if (recordStatus === 'starting') {
      return (
        <>
          <Icon iconName="recording" size="32" />
          <div className="pulse-ring pulse-ring-1" />
          <div className="pulse-ring pulse-ring-2" />
          <div className="pulse-ring pulse-ring-listening" />
        </>
      );
    }
    return <Icon iconName="blackMic" size="24" />;
  };

  useEffect(() => {
    if (isRecording) {
      chunksRef.current = [];
      handleStartRecording();
    }
  }, [isRecording]);

  return (
    <div className="o-chatBotFooter_area_input o-chatBotFooter_speechToText">
      <button
        type="button"
        className="o-chatBotFooter_speechToText_button o-chatBotFooter_speechToText_button-trash"
        onClick={() => handleCleanRecording({ noRecording: true })}
      >
        <Icon iconName="grayClose" size="20x20" />
      </button>

      <div className="o-chatBotFooter_area_input_recordText">
        {recordStatus === 'noRecord' && <span>{t('chatbot.permissionMicRequesting')}</span>}
        {recordStatus === 'starting' ? (
          <div className="o-chatBotFooter_area_input_soundwave">
            <canvas ref={canvasRef} width={200} height={32} />
            <p id="recording-timer" className="o-chatBotFooter_area_input_soundwave_counter">
              00:00
            </p>
          </div>
        ) : (
          <span>{t('chatbot.processing')}</span>
        )}
      </div>
      <button
        type="button"
        className={mapModifiers(
          'o-chatBotFooter_speechToText_button',
          'mic',
          recordStatus === 'starting' && 'recording',
          listening && 'listening'
        )}
        disabled={recordStatus === 'processing' || disabled}
        onClick={handleOnMic}
      >
        {uploading ? <LoadingSpinner color={mainColor} /> : detectMicButton()}
      </button>
    </div>
  );
};

export default SpeechToText;
